import { InitDetail } from 'lightgallery/lg-events';
import { LightGallery as ILightGallery } from 'lightgallery/lightgallery';
import { IPhoto } from 'pages/SchedulerList/components/Inspection/Inspection.types';
import React, { useCallback, useEffect, useRef } from 'react';
import { useInvertImageColor } from '../Actions/useInvertColorImage';
import { useEditButton } from '../Actions/useEditButton';

interface IUseLightGalleryProps {
  photos: IPhoto[];
  onEditClick: () => void;
}

export const useLightGallery = ({ photos, onEditClick }: IUseLightGalleryProps) => {
  const lightGallery = useRef<ILightGallery | null>(null);
  const invertImageColor = useInvertImageColor(lightGallery);
  const editButton = useEditButton(lightGallery, onEditClick);

  const onInit = useCallback((detail: InitDetail) => {
    lightGallery.current = detail.instance;
  }, []);

  const onBeforeOpen = useCallback(() => {
    invertImageColor();
    editButton();
  }, [invertImageColor, editButton]);

  const onAfterClose = useCallback(() => {
    const floating = document.getElementById('floating-button');
    if (floating) {
      floating.style.display = 'none';
    }
  }, []);

  const handleImageContainerClick = useCallback((index: number, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    lightGallery.current?.openGallery(index, e.currentTarget as HTMLElement);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lg = params.get('lg');
    const slide = params.get('slide');
    if (lg && slide && lightGallery.current) {
      setTimeout(() => {
        lightGallery.current?.openGallery(Number.parseInt(slide, 10));
      }, 500);
    }
  }, []);

  const dynamicEl = photos?.map((photo) => ({
    src: photo.url,
    thumb: photo.url,
    subHtml: `<p id="${photo.id}">${photo.description || ''}</p>`,
    fileId: photo.id,
  }));

  return {
    lightGallery,
    onInit,
    onBeforeOpen,
    onAfterClose,
    handleImageContainerClick,
    dynamicEl,
  };
};
